import { usePrescriptionStore } from '../../stores/prescriptionStore';
import { getPlatformType } from './getPlatformType';

/**
 * Remove chaves com valor `undefined` ou `null` do objeto de propriedades.
 *
 * Como estas propriedades têm precedência sobre as do payload no enrichment
 * plugin, enviar `undefined`/`null` sobrescreveria valores válidos já presentes
 * no evento. Omitir a chave preserva o valor existente quando o dado ainda não
 * está disponível no store (ex.: antes da prescrição carregar).
 *
 * @param properties - Conjunto de propriedades candidatas a envio
 * @returns Novo objeto contendo apenas as chaves com valor definido
 */
function stripEmpty(properties: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(properties).filter(([, value]) => value !== undefined && value !== null)
  );
}

/**
 * Monta o conjunto de propriedades default de parceiro, subparceiro e prescritor
 * usado para segmentação no Amplitude.
 *
 * É a fonte única de verdade dessas propriedades: consumida tanto pelo enrichment
 * plugin (aplicadas a todo evento) quanto pelas user properties. Lê o estado atual
 * do `usePrescriptionStore` no momento da chamada — por isso deve ser invocada em
 * tempo de evento/identify, não no carregamento do módulo.
 *
 * @returns Propriedades prontas para o Amplitude, sem chaves de valor vazio
 */
export function buildDefaultAmplitudeProperties(): Record<string, unknown> {
  const state = usePrescriptionStore.getState();
  const prescription = state.prescription;
  const origem = prescription?.Origem;
  const prescritor = prescription?.Prescritor;
  const ownedCertificates = state.ownedCertificates ?? [];

  // `status === 'S'` indica um certificado digital efetivamente ativo/emitido
  // para o prescritor; os demais representam apenas certificadoras disponíveis.
  const activeCertificates = ownedCertificates.filter((certificate) => certificate.status === 'S');

  return stripEmpty({
    partner_id: origem?.idParceiro,
    partner_name: origem?.Nome,
    subpartner_name: origem?.SubParceiro,
    // O backend não expõe um id de subparceiro dedicado; `SubParceiro` já é o
    // identificador usado para o subparceiro em todo o fluxo de emissão.
    subpartner_id: origem?.SubParceiro,

    platform_type: getPlatformType(),

    prescriber_name: prescritor?.Nome,
    prescriber_id: prescritor?.idPrescritor,
    prescriber_email: prescritor?.Email,
    prescriber_specialty: prescritor?.Especialidades,
    prescriber_crm: prescritor?.Numero,
    prescriber_crm_uf: prescritor?.UFConselho,
    prescriber_certificate: activeCertificates.length > 0,
    prescriber_certificate_type: activeCertificates.map((certificate) => certificate.name),
  });
}
